import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';

const PERIOD_OPTIONS = [
  { value: 'mes', label: 'Mês atual' },
  { value: 'trimestre', label: 'Trimestre atual' },
  { value: 'ano', label: 'Ano atual' },
  { value: '12', label: 'Últimos 12 meses' },
];

function formatDt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

export default function SafetyHuddleHistoricoPage() {
  const { sectorId, templateId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();

  const [periodPreset, setPeriodPreset] = useState('12');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const buildQuery = useCallback(() => {
    if (periodPreset === '12') return 'months=12';
    return `period=${periodPreset}`;
  }, [periodPreset]);

  const load = useCallback(async () => {
    if (!unidadeSelecionada?.codigo) {
      setError('Selecione uma unidade.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const d = await shJson(
        `/api/safety-huddle/templates/${templateId}/submissions?${buildQuery()}&limit=200`,
        unidadeSelecionada
      );
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [templateId, unidadeSelecionada, buildQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (submissionId) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetail(null);
    try {
      const d = await shJson(
        `/api/safety-huddle/submissions/${submissionId}`,
        unidadeSelecionada
      );
      setDetail(d);
    } catch (e) {
      setDetail({ error: e.message });
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/safety-huddle/setores/${sectorId}`)}
        sx={{ mb: 2 }}
      >
        Voltar ao setor
      </Button>

      <Typography variant="h5" fontWeight={700} color="primary.main">
        Histórico — {data?.template?.name || 'Formulário'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Envios da unidade no período selecionado
        {data?.period?.label ? ` · ${data.period.label}` : ''}
      </Typography>

      <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel>Período</InputLabel>
        <Select
          label="Período"
          value={periodPreset}
          onChange={(e) => setPeriodPreset(e.target.value)}
        >
          {PERIOD_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Chip
            size="small"
            color="primary"
            label={`${data?.total || 0} envio(s) no período`}
            sx={{ mb: 2 }}
          />
          <Paper elevation={0} sx={{ overflowX: 'auto', border: '1px solid rgba(25,118,210,0.12)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Identificador</TableCell>
                  <TableCell>Referência</TableCell>
                  <TableCell>Enviado em</TableCell>
                  <TableCell>Autor</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.items || []).map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.subjectLabel || '—'}</TableCell>
                    <TableCell>{row.referenceDate || '—'}</TableCell>
                    <TableCell>{formatDt(row.createdAt)}</TableCell>
                    <TableCell>{row.createdByNome || '—'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => openDetail(row.id)}
                      >
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!data?.items?.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Alert severity="info" sx={{ my: 1 }}>
                        Nenhum envio neste período.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Detalhe do envio #{detail?.id || '…'}</DialogTitle>
        <DialogContent>
          {detailLoading && <CircularProgress size={24} />}
          {detail?.error && <Alert severity="error">{detail.error}</Alert>}
          {detail && !detail.error && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {formatDt(detail.createdAt)} · {detail.createdByNome || '—'}
                {detail.referenceDate ? ` · ref ${detail.referenceDate}` : ''}
              </Typography>
              {(detail.answers || []).map((a) => (
                <Box key={a.questionId} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {a.label}
                    {a.isSubjectIdentifier ? ' (identificador)' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {a.responseType === 'status' && (a.statusValue || '—')}
                    {a.responseType === 'checkbox' && (a.checkboxValue ? 'Sim' : 'Não')}
                    {a.responseType === 'numero' &&
                      (a.numberValue != null ? String(a.numberValue) : '—')}
                    {a.responseType === 'data' && (a.dateValue || '—')}
                    {(a.responseType === 'texto' || a.responseType === 'selecao') &&
                      (a.textValue || '—')}
                    {!['status', 'checkbox', 'numero', 'data', 'texto', 'selecao'].includes(
                      a.responseType
                    ) && (a.textValue || a.dateValue || '—')}
                    {a.notes ? ` · Obs: ${a.notes}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
